/**
 * tabs.ts — module switching (P1 US-118).
 *
 * Ported from oracle lines 1230–1236: `courant` and `onglet(m)`.
 *
 * Small, and load-bearing in one way that is easy to miss: `courant` is not just which pane is
 * visible. It is also **what `sauver` uses for the `onglet` field and for the download filename**
 * (oracle lines 1202 and 1206), so a tab implementation that tracked visibility without updating
 * this state would produce saved files that always reopen on the wrong tab.
 */

import { $$ } from './dom';
import type { FicheModule } from './fiches';

export const MODULES: readonly FicheModule[] = ['lamine', 'creux', 'bois'];

export function isModule(value: unknown): value is FicheModule {
  return typeof value === 'string' && (MODULES as readonly string[]).includes(value);
}

/**
 * The tab controller.
 *
 * `onShow` fires when a module becomes visible, and it is how US-113's cold-start rule is honoured:
 * the oracle calculated all three modules at boot (line 1238), including the two hidden panes, and
 * this port calculates a hidden module only when its tab is first shown. `onShow` therefore fires
 * for a first reveal and not for a return visit — a module that already has a result on screen does
 * not need to spend a request re-deriving it.
 */
export function tabs(initial: FicheModule, onShow: (mod: FicheModule) => void) {
  let courant: FicheModule = initial;
  const shown = new Set<FicheModule>([initial]);

  function onglet(mod: FicheModule): void {
    courant = mod;

    for (const tab of $$<HTMLButtonElement>('.tab')) {
      tab.classList.toggle('on', tab.dataset['mod'] === mod);
    }
    for (const pane of $$<HTMLElement>('.pane')) {
      pane.hidden = pane.dataset['mod'] !== mod;
    }

    if (!shown.has(mod)) {
      shown.add(mod);
      onShow(mod);
    }
  }

  return {
    onglet,
    /** The active module. Read by save, for both the `onglet` field and the filename. */
    get courant(): FicheModule {
      return courant;
    },
    /**
     * Mark a module as already rendered without switching to it.
     *
     * Used when opening a saved file: every module in the file is recalculated there, so revealing
     * one afterwards must not trigger a second request for a result that is already on screen.
     */
    markShown(mod: FicheModule): void {
      shown.add(mod);
    },
  };
}
